import type { MittwaldAPIV2Client } from "@mittwald/api-client-NEXT";
import { extractTotalCountHeader } from "@mittwald/api-client-NEXT";
import { assertOneOfStatus, assertStatus } from "@mittwald/api-client-NEXT";
import { checkForValidationErrors } from "../../../base/ValidationError";
import type { AppInstallationBehaviors } from "./types.js";

export const apiAppInstallationBehaviors = (
  client: MittwaldAPIV2Client,
): AppInstallationBehaviors => ({
  find: async (appInstallationId) => {
    const response = await client.app.getAppinstallation({ appInstallationId });
    if (response.status === 200) {
      return response.data;
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    assertOneOfStatus(response, [404, 403]);
  },

  list: async (projectId, query) => {
    const response = await client.app.listAppinstallations({
      queryParameters: query,
      projectId,
    });
    assertStatus(response, 200);
    return {
      items: response.data,
      totalCount: extractTotalCountHeader(response),
    };
  },

  listForUser: async (query) => {
    const response = await client.app.listAppinstallationsForUser({
      queryParameters: query,
    });
    assertStatus(response, 200);
    return {
      items: response.data,
      totalCount: extractTotalCountHeader(response),
    };
  },

  create: async (projectId, data) => {
    const response = await client.app.requestAppinstallation({
      projectId,
      data,
    });

    const validationErrors = checkForValidationErrors(response);
    if (validationErrors) {
      return validationErrors;
    }

    assertStatus(response, 201);

    return response.data;
  },

  update: async (appInstallationId, data) => {
    const response = await client.app.patchAppinstallation({
      appInstallationId,
      data,
    });

    assertStatus(response, 204);
  },

  delete: async (appInstallationId) => {
    const response = await client.app.uninstallAppinstallation({
      appInstallationId,
    });

    assertStatus(response, 204);
  },

  linkDatabase: async (appInstallationId, data) => {
    const response = await client.app.linkDatabase({
      appInstallationId,
      data,
    });

    assertStatus(response, 204);
  },

  unlinkDatabase: async (appInstallationId, databaseId) => {
    const response = await client.app.unlinkDatabase({
      appInstallationId,
      databaseId,
    });

    assertStatus(response, 204);
  },

  replacePrimaryDatabase: async (appInstallationId, data) => {
    const response = await client.app.replaceDatabase({
      appInstallationId,
      data,
    });

    assertStatus(response, 204);
  },

  copy: async (appInstallationId, data) => {
    const response = await client.app.requestAppinstallationCopy({
      appInstallationId,
      data,
    });

    assertStatus(response, 201);
  },

  getInstalledSystemSoftware: async (appInstallationId, query) => {
    const response =
      await client.app.getInstalledSystemsoftwareForAppinstallation({
        appInstallationId,
        queryParameters: query,
      });

    assertStatus(response, 200);

    return response.data;
  },
});
